import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MessageCircle, FlaskConical, Bell, FileText, Clock, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";

export function IntegrationsSettings() {
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Integrations</CardTitle>
          <CardDescription>
            Connect AM CARE with external services for your hospital. Settings apply only to
            your clinic account.
          </CardDescription>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          WhatsApp quick-share is available from the patient queue and patient profile. Staff
          should install WhatsApp on their phone or keep WhatsApp Web logged in on desktop before
          sending messages or sharing PDF prescriptions.
        </CardContent>
      </Card>

      <div className="grid gap-6 md:grid-cols-2">
        <Card className="overflow-hidden border-green-100">
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between gap-3">
              <div className="rounded-lg p-2.5 bg-green-50 text-green-600">
                <MessageCircle className="h-6 w-6" />
              </div>
              <Badge className="shrink-0 bg-green-100 text-green-800 hover:bg-green-100">
                Available
              </Badge>
            </div>
            <CardTitle className="text-xl pt-2">WhatsApp</CardTitle>
            <CardDescription>
              Message patients from your staff phone using pre-filled templates. No API keys
              required — opens WhatsApp Web or the mobile app.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-sm font-medium text-foreground mb-2">How to use</p>
              <ul className="space-y-2">
                {[
                  "Queue: tap the green WhatsApp icon on a patient card",
                  "Patient profile: Quick Actions → WhatsApp, or icon next to contact",
                  "Use WhatsApp app (phone) or WhatsApp Web (desktop) before starting quick-share",
                  "Pick a template, review the text, then tap Send in WhatsApp",
                  "For prescriptions, choose Share PDF and send the generated file in WhatsApp",
                ].map((feature) => (
                  <li
                    key={feature}
                    className="flex items-start gap-2 text-sm text-muted-foreground"
                  >
                    <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5 text-green-600" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
            <Button variant="outline" className="w-full" asChild>
              <Link to="/queue">Open patient queue</Link>
            </Button>
          </CardContent>
        </Card>

        <Card className="overflow-hidden">
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between gap-3">
              <div className="rounded-lg p-2.5 bg-violet-50 text-violet-600">
                <FlaskConical className="h-6 w-6" />
              </div>
              <Badge variant="secondary" className="shrink-0">
                Coming soon
              </Badge>
            </div>
            <CardTitle className="text-xl pt-2">Laboratory</CardTitle>
            <CardDescription>
              Connect your diagnostic lab to send orders and receive results inside the patient
              record.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-sm font-medium text-foreground mb-2">Planned features</p>
              <ul className="space-y-2">
                {[
                  "Create lab orders from consultations",
                  "Attach results to patient history",
                  "Track pending and completed tests",
                ].map((feature) => (
                  <li
                    key={feature}
                    className="flex items-start gap-2 text-sm text-muted-foreground"
                  >
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-hospital-primary" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
            <Button variant="outline" className="w-full" disabled>
              Connect Laboratory
            </Button>
          </CardContent>
        </Card>
      </div>

      <Card className="border-dashed">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Bell className="h-4 w-4 text-hospital-primary" />
            What works today
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-2">
          <p className="flex items-center gap-2">
            <MessageCircle className="h-4 w-4 shrink-0 text-green-600" />
            WhatsApp quick-share — Queue and patient profile
          </p>
          <p className="flex items-center gap-2">
            <FileText className="h-4 w-4 shrink-0" />
            Clinic profile, logo, and wallpaper — Settings → Profile
          </p>
          <p className="flex items-center gap-2">
            <Clock className="h-4 w-4 shrink-0" />
            Staff, doctors, and services — Settings → Users / Services
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
